import React, { useState, useRef, useEffect } from 'react';
export default function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Type to search...',
  style = {},
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const normalised = options.map(o =>
    typeof o === 'string'
      ? { value: o, label: o }
      : { value: o.value ?? o.id ?? '', label: o.label ?? o.name ?? o.batch_name ?? o.college_name ?? o.course_name ?? '' }
  );
  const selected = normalised.find(o => o.value === value);
  const filtered = query
    ? normalised.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : normalised;

  useEffect(() => {
    if (!open) return;
    function handle(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  function openDropdown() {
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function closeDropdown() {
    setOpen(false);
    setQuery('');
  }

  function handleSelect(val) {
    onChange(val);
    setOpen(false);
    setQuery('');
    if(inputRef.current) inputRef.current.blur();
  }

  function handleClear(e) {
    e.stopPropagation();
    onChange('');
    closeDropdown();
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', ...style }}>
      <div
        onClick={() => open ? closeDropdown() : openDropdown()}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#ffffff', border: '1.5px solid var(--border)',
          borderColor: open ? 'var(--accent)' : 'var(--border)',
          borderRadius: 'var(--radius)', padding: '8px 12px',
          cursor: 'pointer', fontSize: 13, color: (selected || value) ? 'var(--text)' : 'var(--text3)',
          minWidth: 160, userSelect: 'none',
          boxShadow: open ? '0 0 0 3px rgba(0,176,155,0.12)' : 'none',
          transition: 'all 0.15s ease',
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : value ? value : placeholder}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8, flexShrink: 0 }}>
          {value && (
            <span onClick={handleClear}
              style={{ fontSize: 14, color: 'var(--text3)', lineHeight: 1, padding: '0 2px', cursor: 'pointer' }}
              title="Clear">×</span>
          )}
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
            style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', color: 'var(--text3)' }}>
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          background: '#ffffff', border: '1.5px solid var(--accent)',
          borderRadius: 'var(--radius)', marginTop: 4,
          boxShadow: '0 8px 24px rgba(0,176,155,0.15)',
          overflow: 'hidden', minWidth: 200,
        }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: 'var(--bg3)' }}>
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              onKeyDown={e => { if (e.key === 'Escape') closeDropdown(); }}
              style={{
                width: '100%', border: '1px solid var(--border)', borderRadius: 6,
                padding: '5px 8px', fontSize: 12, background: '#fff',
                outline: 'none', color: 'var(--text)',
              }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <div
              onClick={() => handleSelect('')}
              style={{
                padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                color: !value ? 'var(--accent)' : 'var(--text3)',
                fontWeight: !value ? 600 : 400,
                background: !value ? 'var(--accent-light)' : 'transparent',
                borderBottom: '1px solid var(--border)',
              }}
            >{placeholder}</div>
            {filtered.length === 0 ? (
              <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text3)', textAlign: 'center' }}>
                No results found
              </div>
            ) : filtered.map(opt => (
              <div
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                style={{
                  padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                  color: opt.value === value ? 'var(--accent)' : 'var(--text)',
                  fontWeight: opt.value === value ? 600 : 400,
                  background: opt.value === value ? 'var(--accent-light)' : 'transparent',
                  borderBottom: '1px solid var(--bg3)',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (opt.value !== value) e.currentTarget.style.background = 'var(--bg3)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = opt.value === value ? 'var(--accent-light)' : 'transparent'; }}
              >
                {query ? highlightMatch(opt.label, query) : opt.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
function highlightMatch(text, query) {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ background: 'rgba(0,176,155,0.25)', borderRadius: 2, fontWeight: 700 }}>
        {text.slice(idx, idx + query.length)}
      </span>
      {text.slice(idx + query.length)}
    </>
  );
}
